<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells>
        <app-input
          @input.native="onUsersSearch"
          v-model="search"
          class="input--200"
          type="search"
          placeholder="Search by name"
          name="search"
        />
        <app-filters
          v-model="lead_filter"
          :filters="lead_filters"
          name="filterLead"
          @change="onChangeFilter"
        />
        <app-checkbox v-if="is_sales_manager" @change="onChangeFilter" v-model="filter_my_group" label="My group"/>
      </app-cells>
      <div v-for="client_table_item of client_table" :key="client_table_item.name" class="client-block">
        <app-cells v-if="client_table_item.name">
          <h2 class="title">{{ client_table_item.name }}</h2>
          <div v-if="client_table_item.today" class="status-title" :style="{ 'backgroundColor': '#77b017' }">Today</div>
        </app-cells>
        <v-client-table
          :data="client_table_item.items"
          :columns="table.columns"
          :options="table.options"
          class="table-default"
          :class="{ 'table-default--loading': table_preloader }"
        >
          <template #h__name><div class="table-default__left">Name</div></template>
          <template #h__lead><div class="table-default__left">Status</div></template>
          <template #asana="{ row }">
            <div class="table-default__controls">
              <a
                :href="row?.lead?.asana"
                target="_blank"
                class="table-default__activity table-default__activity--asana"
                :class="{'table-default__activity--active': row.lead?.asana}"
              />
              <a
                :href="`https://story.altezza.travel/clients/${row.lead?.final_itinerary?.itinerary_id}`"
                target="_blank"
                class="table-default__activity table-default__activity--itinerary"
                :class="{ 'table-default__activity--active': row.lead?.final_itinerary?.itinerary_id }"
              />
              <a
                :href="`https://story.altezza.travel/itinerary/${row.lead?.final_itinerary?.hash}`"
                target="_blank"
                class="table-default__activity table-default__activity--clients"
                :class="{ 'table-default__activity--active': row.lead?.final_itinerary?.hash }"
              />
            </div>
          </template>
          <template #activities="{ row }">
            <div class="table-default__controls">
              <span
                v-tooltip="{
                  content: activitiesTooltipContent(row.activities.climbing_count, {start: row.tour_dates.first_climbing_activity, end: row.tour_dates.last_climbing_activity} ),
                  placement: 'left',
                  offset: 10,
                  html: true
                }"
                class="table-default__activity table-default__activity--climbing"
                :class="{'table-default__activity--active': row.activities.climbing_count}"
              >
              </span>
              <span
                v-tooltip="{
                  content: activitiesTooltipContent(row.activities.safari_count, {start: row.tour_dates.first_safari_activity, end: row.tour_dates.last_safari_activity} ),
                  placement: 'left',
                  offset: 10,
                  html: true
                }"
                class="table-default__activity table-default__activity--safari"
                :class="{'table-default__activity--active': row.activities.safari_count}"
              >
              </span>
              <span
                v-tooltip="{
                  content: activitiesTooltipContent(row.activities.day_trips_count, {start: row.tour_dates.first_day_trip_activity, end: row.tour_dates.last_day_trip_activity} ),
                  placement: 'left',
                  offset: 10,
                  html: true
                }"
                class="table-default__activity table-default__activity--day_trips"
                :class="{'table-default__activity--active': row.activities.day_trips_count}"
              >
              </span>
              <span
                v-tooltip="{
                  content: activitiesTooltipContent(row.activities.island_vacation_count, {start: row.tour_dates.first_island_vacation_activity, end: row.tour_dates.last_island_vacation_activity} ),
                  placement: 'left',
                  offset: 10,
                  html: true
                }"
                class="table-default__activity table-default__activity--island"
                :class="{'table-default__activity--active': row.activities.island_vacation_count}"
              >
              </span>
              <span
                v-tooltip="{
                  content: activitiesTooltipContent(row.activities.free_days_count, {start: row.tour_dates.first_free_days_activity, end: row.tour_dates.last_free_days_activity} ),
                  placement: 'left',
                  offset: 10,
                  html: true
                }"
                class="table-default__activity table-default__activity--free_days"
                :class="{'table-default__activity--active': row.activities.free_days_count}"
              >
              </span>
            </div>
          </template>
          <template #name="{ row }">
            <div class="table-default__left table-default__controls">
              <div
                v-tooltip="{ content: row.payment_control_info.note, placement: 'top-start', offset: 10 }"
                class="table-default__activity table-default__activity--notes"
                :class="{'table-default__activity--active': row.payment_control_info.note}"
              ></div>
              <router-link :to="{name: 'clients-groups-payment-control', params: {id: row.id }}" class="table-default__link-border">
                {{ row.name }}
              </router-link>
              <span
                v-tooltip="{ content: clientTableList(row.clients), placement: 'top-start', offset: 10, html: true }"
                :style="{cursor: 'pointer'}"
              >
                ({{ row.pax }})
              </span>
              <span
                v-tooltip="{ content: `${row.lead?.tour_operator ? row.lead.tour_operator.name : ''}`, placement: 'top-start', offset: 10, html: true }"
                :style="{cursor: 'pointer'}"
              >
                {{ row?.lead?.tour_operator ? `(${row.lead.tour_operator.short_name})` : ''}}
              </span>
            </div>
          </template>
          <template #lead="{ row }">
            <div class="table-default__left table-default__controls">
              <span v-if="row.lead" class="status-tag" :style="{ 'backgroundColor': row.lead.status.color }">
                {{ row.lead.status.name }}
              </span>
              <span v-else>-</span>
            </div>
          </template>
          <template #tour_dates="{ row }">
            <span v-if="row.tour_dates.first_activity && row.tour_dates.last_activity">
              {{ dateFormat(row.tour_dates.first_activity) }} - {{ dateFormat(row.tour_dates.last_activity) }}
            </span>
            <span v-else>-</span>
          </template>
          <template #arrival="{ row }">
            <span v-if="row.transport.arrival && row.transport.departure">
              {{ dateFormat(row.transport.arrival) }} - {{ dateFormat(row.transport.departure) }}
            </span>
            <span v-else>-</span>
          </template>
          <template #amount="{ row }">
            {{ row.lead?.final_itinerary ? `${row.lead.final_itinerary.itinerary_amount_usd.toLocaleString()} USD` : '-' }}
          </template>
          <template #paid="{ row }">
            {{ row.lead?.final_itinerary ? `${row.lead.final_itinerary.invoices_amount_paid_usd.toLocaleString()} USD` : '-' }}
          </template>
          <template #balance="{ row }">
            <span
              v-if="row.lead?.final_itinerary"
              class="status-tag"
              :style="{ 'backgroundColor': row.lead.final_itinerary.balance === 0 ? '#77b017' : row.lead.final_itinerary.balance < 0 ? '#e85c20' : '#439ad1' }"
            >
              {{ row.lead.final_itinerary.balance.toLocaleString() }} USD
            </span>
            <span v-else>-</span>
          </template>
          <template #payable="{ row }">
            <template v-if="row.lead?.final_itinerary">
              <span
                v-if="!row.lead.final_itinerary.payable_amount_cover_balance"
                class="status-tag"
                :style="{ 'backgroundColor': '#e85c20' }"
              >
              {{ row.lead.final_itinerary.invoices_amount_payable_usd.toLocaleString() }} USD
              </span>
              <span v-else>{{ row.lead.final_itinerary.invoices_amount_payable_usd.toLocaleString() }} USD</span>
            </template>
            <span v-else>-</span>
          </template>
          <template #invoices="{ row }">
            <span
              v-if="row.lead?.final_itinerary"
              class="status-tag"
              :style="{ 'backgroundColor': row.lead.final_itinerary.invoices_checked === row.lead.final_itinerary.invoices_count ? '#77b017' : '#e85c20' }"
            >
              {{ row.lead.final_itinerary.invoices_checked }} / {{ row.lead.final_itinerary.invoices_count }}
            </span>
            <span v-else>-</span>
          </template>
          <template #created_by="{ row }">
            <div class="table-default__controls">
              <div class="table-default__photo">
                <img
                  :src="getUserAvatar(row.created_by.crop.crop_url)"
                  alt="Avatar"
                  v-tooltip="{ content: row.created_by.full_name, placement: 'top-start', offset: 10 }"
                >
              </div>
            </div>
          </template>
        </v-client-table>
      </div>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table_length" :page="pagination.page" :limit="40" />
      </app-cells>
    </template>

  </div>
</template>

<script>
import { getClientsGroupsPaymentControlByDates } from '@/http'
import debounce from 'lodash/debounce'
import { format, parse } from 'date-fns'

export default {
  name: 'PaymentControlGroups',
  data() {
    return {
      is_loaded: false,
      search: '',
      client_table: [],
      table_length: 0,
      table: {
        items: [],
        columns: [
          'asana',
          'activities',
          'name',
          'lead',
          'tour_dates',
          'arrival',
          'amount',
          'paid',
          'balance',
          'payable',
          'invoices',
          'created_by'
        ],
        options: {
          headings: {
            asana: 'External',
            activities: 'Activities',
            name: 'Name',
            lead: 'Status',
            tour_dates: 'Tour dates',
            arrival: 'Travel dates',
            amount: 'Amount',
            paid: 'Paid',
            balance: 'Balance',
            payable: 'Payable',
            invoices: 'Invoices',
            created_by: 'Manager',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      lead_filter: 'not_checked',
      lead_filters: [
        { value: 'not_checked', label: 'Not checked' },
        { value: 'checked', label: 'Checked' },
      ],
      table_preloader: false,
      filter_my_group: true,
    }
  },
  computed: {
    is_sales_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    fetchItems() {
      getClientsGroupsPaymentControlByDates({
        page: this.pagination.page,
        page_size: 40,
        search: this.search,
        payment_control_completed: this.search ? '' : this.lead_filter === 'checked',
        range_type: 'month',
        group_by: 'tour_dates__first_activity',
        activities: true,
        category: 'next_two_weeks',
        ordering: this.lead_filter === 'checked' ? '-first_activity,-arrival' : '',
        my: this.is_sales_manager ? this.filter_my_group : '',
      }).then(res => {
        this.client_table = res.data.results.length ? res.data.results : [{name: '', items: []}]
        this.pagination.pages = res.data.pages
        this.pagination.count = res.data.count
        this.table_length = 0
        res.data.results.forEach(item => {
          this.table_length += item.items.length
        })
        this.is_loaded = true
        this.table_preloader = false
      })
    },
    onChangeFilter() {
      this.search = ''
      this.pagination.page = 1
      this.fetchItems()
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    activitiesTooltipContent(pax, dates) {
      return `${this.dateFormat(dates.start)} - ${this.dateFormat(dates.end)}<br>Pax: ${pax}`
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM') : ''
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    clientTableList(clients) {
      let result = ''
      for (const key in clients) {
        result += `${clients[key].first_name} ${clients[key].last_name}<br>`
      }
      return result
    },
    onUsersSearch() {
      this.searchUsers(this)
    },
    searchUsers: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
  }
}
</script>

<style lang="sass" scoped>
.client-block
  margin-bottom: 30px

  &:last-child
    margin-bottom: 0
</style>
